/**
 * @fileoverview Koala Analytics Integration
 * 
 * This module provides utilities for integrating with Koala Analytics to track
 * user activity and identify customers throughout their journey on the website
 * and application.
 * 
 * Key Features:
 * - User identification for logged-in users
 * - Event tracking for user actions
 * - TypeScript support for Koala SDK
 * 
 * @author Claude Code
 * @since 2024-01-01
 */

// Extend the global window object to include Koala
declare global {
  interface Window {
    ko?: {
      identify: (email: string, traits?: Record<string, unknown>) => void;
      track: (event: string, properties?: Record<string, unknown>) => void;
      ready: (callback: () => void) => void;
      qualify: (traits: Record<string, unknown>) => void;
      removeListeners: () => void;
      on: (event: string, callback: (data: unknown) => void) => void;
      off: (event: string, callback: (data: unknown) => void) => void;
    };
  }
}

/**
 * User traits interface for Koala identification
 */
interface KoalaUserTraits {
  name?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  organizationId?: string;
  organizationName?: string;
  onboardingCompleted?: boolean;
  isStaff?: boolean;
  lastActive?: string;
  [key: string]: unknown;
}

/**
 * Event properties interface for Koala tracking
 */
interface KoalaEventProperties {
  userId?: string;
  email?: string;
  organizationId?: string;
  [key: string]: unknown;
}

/**
 * Check if Koala SDK is available
 */
export function isKoalaAvailable(): boolean {
  return typeof window !== 'undefined' && !!window.ko;
}

/**
 * Identify a user in Koala Analytics
 * 
 * This function should be called whenever you have a user's email address
 * and want to associate their anonymous activity with their identity.
 * 
 * @param email - The user's email address (required)
 * @param traits - Additional user traits and properties
 */
export function identifyUser(email: string, traits: KoalaUserTraits = {}): void {
  if (!isKoalaAvailable()) {
    if (import.meta.env.DEV) {
      console.warn('[Koala] SDK not available, skipping identify call');
    }
    return;
  }

  try {
    if (import.meta.env.DEV) {
      console.log('[Koala] Identifying user:', {
        email,
        traits: Object.keys(traits)
      });
    }

    window.ko!.identify(email, traits);
  } catch (error) {
    console.error('[Koala] Failed to identify user:', error);
  }
}

/**
 * Track an event in Koala Analytics
 * 
 * @param event - The event name
 * @param properties - Event properties
 */
export function trackEvent(event: string, properties: KoalaEventProperties = {}): void {
  if (!isKoalaAvailable()) {
    if (import.meta.env.DEV) {
      console.warn('[Koala] SDK not available, skipping track call');
    }
    return;
  }

  try {
    if (import.meta.env.DEV) {
      console.log('[Koala] Tracking event:', {
        event,
        properties: Object.keys(properties)
      });
    }

    window.ko!.track(event, properties);
  } catch (error) {
    console.error('[Koala] Failed to track event:', error);
  }
}

/**
 * Qualify a user with additional traits
 * 
 * @param traits - User traits for qualification
 */
export function qualifyUser(traits: KoalaUserTraits): void {
  if (!isKoalaAvailable()) {
    if (import.meta.env.DEV) {
      console.warn('[Koala] SDK not available, skipping qualify call');
    }
    return;
  }

  try {
    if (import.meta.env.DEV) {
      console.log('[Koala] Qualifying user with traits:', Object.keys(traits));
    }

    window.ko!.qualify(traits);
  } catch (error) {
    console.error('[Koala] Failed to qualify user:', error);
  }
}

/**
 * Execute a callback when Koala SDK is ready
 * 
 * @param callback - Function to execute when ready
 */
export function onReady(callback: () => void): void {
  if (!isKoalaAvailable()) {
    if (import.meta.env.DEV) {
      console.warn('[Koala] SDK not available, executing callback immediately');
    }
    callback();
    return;
  }

  try {
    window.ko!.ready(callback);
  } catch (error) {
    console.error('[Koala] Failed to register ready callback:', error);
    // Execute callback anyway to prevent blocking
    callback();
  }
}